import type en from '../en/users'
import type { Translation } from '../types'

/** People, roles and permissions */
const users: Translation<typeof en> = {
  "users.removed": "Benutzer entfernt",
  "users.removeFailed": "Der Benutzer konnte nicht entfernt werden",
  "users.vpnUpdated": "VPN-Status aktualisiert",
  "users.vpnFailed": "Das VPN konnte nicht umgeschaltet werden",
  "users.inviteSent": "Einladung gesendet",
  "users.inviteSentAgain": "Eine neue Einladungs-E-Mail wurde gesendet.",
  "users.resendFailed": "Die Einladung konnte nicht erneut gesendet werden",
  "users.reset2faDone": "Zwei-Faktor-Anmeldung zurückgesetzt",
  "users.reset2faText": "Die Person wird überall abgemeldet und richtet sie bei der nächsten Anmeldung neu ein.",
  "users.resetFailed": "Zurücksetzen fehlgeschlagen",
  "users.title": "Benutzer",
  "users.count": { one: "{count} Teammitglied", other: "{count} Teammitglieder" },
  "users.invite": "Benutzer einladen",
  "users.search": "Benutzer suchen",
  "users.searchPlaceholder": "Nach Name oder E-Mail suchen…",
  "users.user": "Benutzer",
  "users.role": "Rolle",
  "users.lastLogin": "Letzte Anmeldung",
  "users.empty": "Keine Benutzer gefunden",
  "users.adminBadge": "Admin",
  "users.twoFactorOn": "Zwei-Faktor-Anmeldung ist aktiv",
  "users.never": "Nie",
  "users.resend": "Einladungs-E-Mail erneut senden",
  "users.suspendVpn": "VPN-Zugang sperren",
  "users.enableVpn": "VPN-Zugang wieder einschalten",
  "users.suspendVpnConfirm": "Das VPN von „{name}“ sperren?",
  "users.enableVpnConfirm": "Das VPN von „{name}“ wieder einschalten?",
  "users.reset2fa": "Zwei-Faktor-Anmeldung zurücksetzen",
  "users.reset2faFor": "Zwei-Faktor-Anmeldung für {name} zurücksetzen",
  "users.reset2faConfirm": "Zwei-Faktor-Anmeldung für „{name}“ zurücksetzen? Die Person wird überall abgemeldet und richtet sie bei der nächsten Anmeldung neu ein.",
  "users.editAccess": "Zugriff bearbeiten",
  "users.removeNamed": "{name} entfernen",
  "users.removeConfirm": "Benutzer „{name}“ entfernen?",
  "users.updated": "Benutzer aktualisiert",
  "users.addedNoMail": "Benutzer hinzugefügt, aber die Einladungs-E-Mail konnte nicht gesendet werden",
  "users.addedNoMailText": "Prüfen Sie die Mail-Einstellungen und verwenden Sie dann „Einladungs-E-Mail erneut senden“.",
  "users.siteAccess": "Zugriff auf Standorte",
  "users.allSites": "Alle Standorte (auch später hinzugefügte)",
  "users.noSites": "Noch keine Standorte",
  "users.noneSelected": "Keine Standorte gewählt – dieser Benutzer sieht dann keinen Standort.",
  "users.updateFailed": "Der Benutzer konnte nicht aktualisiert werden",
  "users.editTitle": "{name} bearbeiten",
  "users.noRole": "Keine Rolle",
  "users.noRoleCustom": "Keine Rolle (eigene Berechtigungen)",
  "users.companyAdmin": "Unternehmensadministrator (voller Zugriff)",
  "users.save": "Speichern",
  "users.inviteFailed": "Der Benutzer konnte nicht eingeladen werden",
  "users.inviteTitle": "Ein Teammitglied einladen",
  "users.fullName": "Vollständiger Name *",
  "users.fullNamePlaceholder": "Erika Mustermann",
  "users.email": "E-Mail-Adresse *",
  "users.emailPlaceholder": "erika@firma.de",
  "users.inviteInfo": "Eine Einladungs-E-Mail mit einem Link zum Festlegen des Passworts wird gesendet. Das VPN-Konto wird automatisch erstellt.",
  "users.sendInvite": "Einladung senden",
  "users.roles.deleted": "Rolle gelöscht",
  "users.roles.deleteFailed": "Die Rolle konnte nicht gelöscht werden",
  "users.roles.title": "Rollen",
  "users.roles.subtitle": "Berechtigungssätze für Ihre Teammitglieder",
  "users.roles.create": "Rolle erstellen",
  "users.roles.system": "SYSTEM",
  "users.roles.editNamed": "{name} bearbeiten",
  "users.roles.deleteNamed": "{name} löschen",
  "users.roles.deleteConfirm": "Rolle „{name}“ löschen?",
  "users.roles.empty": "Noch keine Rollen – erstellen Sie eine, um sie Teammitgliedern zuzuweisen",
  "users.roles.noPermissions": "Keine Berechtigungen vergeben",
  "users.roles.updated": "Rolle aktualisiert",
  "users.roles.updateFailed": "Die Rolle konnte nicht aktualisiert werden",
  "users.roles.savePermissions": "Berechtigungen speichern",
  "users.roles.created": "Rolle erstellt",
  "users.roles.createFailed": "Die Rolle konnte nicht erstellt werden",
  "users.roles.name": "Name der Rolle *",
  "users.roles.namePlaceholder": "Netzwerktechniker",
  "users.roles.description": "Beschreibung",
  "users.roles.descriptionPlaceholder": "Optionale Beschreibung",
  "users.roles.permissions": "Berechtigungen",
  "users.roles.selected": { one: "{count} Berechtigung gewählt", other: "{count} Berechtigungen gewählt" },
  "users.roles.hoverHint": "Zeigen Sie auf eine Berechtigung, um zu sehen, was sie erlaubt",
  "users.group.sites": "Standorte",
  "users.group.monitoring": "Überwachung",
  "users.group.templates": "Vorlagen",
  "users.group.users": "Benutzer",
  "users.group.vpn": "VPN",
  "users.group.company": "Unternehmen",
  "users.group.api": "API",
  "users.permission.SITE_VIEW": "Ansehen",
  "users.permission.SITE_CREATE": "Erstellen",
  "users.permission.SITE_EDIT": "Bearbeiten",
  "users.permission.SITE_DELETE": "Löschen",
  "users.permission.MONITORING_VIEW": "Ansehen",
  "users.permission.TEMPLATE_VIEW": "Ansehen",
  "users.permission.TEMPLATE_CREATE": "Erstellen",
  "users.permission.TEMPLATE_EDIT": "Bearbeiten",
  "users.permission.TEMPLATE_DELETE": "Löschen",
  "users.permission.TEMPLATE_APPLY": "Anwenden",
  "users.permission.USER_VIEW": "Ansehen",
  "users.permission.USER_CREATE": "Einladen",
  "users.permission.USER_EDIT": "Bearbeiten",
  "users.permission.USER_DELETE": "Entfernen",
  "users.permission.VPN_ACCESS": "Zugang",
  "users.permission.VPN_VIEW": "Ansehen",
  "users.permission.VPN_MANAGE": "Verwalten",
  "users.permission.COMPANY_BRANDING": "Branding",
  "users.permission.COMPANY_SETTINGS": "Einstellungen",
  "users.permission.LICENSE_VIEW": "Lizenz ansehen",
  "users.permission.LICENSE_MANAGE": "Lizenz verwalten",
  "users.permission.APIKEY_VIEW": "Ansehen",
  "users.permission.APIKEY_CREATE": "Erstellen",
  "users.permission.APIKEY_DELETE": "Widerrufen",
  "users.permissionDesc.SITE_VIEW": "Die Standortliste und Basisdaten sehen",
  "users.permissionDesc.SITE_CREATE": "Neue Standorte hinzufügen",
  "users.permissionDesc.SITE_EDIT": "Einstellungen und API-Schlüssel eines Standorts ändern",
  "users.permissionDesc.SITE_DELETE": "Standorte endgültig entfernen",
  "users.permissionDesc.MONITORING_VIEW": "Live-Daten, Geräte und Clients sehen",
  "users.permissionDesc.TEMPLATE_VIEW": "Die Vorlagenbibliothek durchsuchen",
  "users.permissionDesc.TEMPLATE_CREATE": "Neue Vorlagen erstellen",
  "users.permissionDesc.TEMPLATE_EDIT": "Bestehende Vorlagen ändern",
  "users.permissionDesc.TEMPLATE_DELETE": "Vorlagen entfernen",
  "users.permissionDesc.TEMPLATE_APPLY": "Vorlagen auf Standorte anwenden",
  "users.permissionDesc.USER_VIEW": "Die Liste der Teammitglieder sehen",
  "users.permissionDesc.USER_CREATE": "Neue Mitglieder einladen",
  "users.permissionDesc.USER_EDIT": "Rollen und Status ändern",
  "users.permissionDesc.USER_DELETE": "Mitglieder aus dem Unternehmen entfernen",
  "users.permissionDesc.VPN_ACCESS": "Das Mitglied erhält ein VPN-Konto",
  "users.permissionDesc.VPN_VIEW": "Die VPN-Seite und den Status der Konten sehen",
  "users.permissionDesc.VPN_MANAGE": "VPN-Konten erstellen, sperren und erneuern",
  "users.permissionDesc.COMPANY_BRANDING": "Logo, Farben und Erscheinungsbild ändern",
  "users.permissionDesc.COMPANY_SETTINGS": "Einstellungen und Mailserver des Unternehmens verwalten",
  "users.permissionDesc.LICENSE_VIEW": "Lizenzstatus und Grenzen sehen",
  "users.permissionDesc.LICENSE_MANAGE": "Die Lizenz verlängern und ändern",
  "users.permissionDesc.APIKEY_VIEW": "Die Liste der API-Schlüssel sehen",
  "users.permissionDesc.APIKEY_CREATE": "Neue API-Schlüssel erstellen",
  "users.permissionDesc.APIKEY_DELETE": "API-Schlüssel widerrufen",
  "users.group.rental": "Vermietung",
  "users.permission.RENTAL_VIEW": "Ansehen",
  "users.permission.RENTAL_PLAN": "Planen",
  "users.permission.STOCK_MANAGE": "Bestand verwalten",
  "users.permission.PURCHASE_APPROVE": "Einkäufe genehmigen und senden",
  "users.permissionDesc.RENTAL_VIEW": "Bestand, Kunden, Veranstaltungen, Planung und Mietpreise sehen",
  "users.permissionDesc.RENTAL_PLAN": "Kunden und Veranstaltungen verwalten: Positionen, Preise, Rabatte und Status",
  "users.permissionDesc.STOCK_MANAGE": "Artikel, Geräte, Cases, Lager, Umlagerungen, Lieferanten, Zumietungen und Bestellungen verwalten und Kosten sehen",
  "users.permissionDesc.PURCHASE_APPROVE": "Bestellungen und Zumietanfragen genehmigen, wodurch sie an den Lieferanten gehen",
  "users.permission.MIKROTIK_PROVISION": "MikroTik-Einheiten bereitstellen",
  "users.permissionDesc.MIKROTIK_PROVISION": "Einen MikroTik-Standort anhand der Seriennummer anlegen oder aktualisieren, sein VPN-Zertifikat ausstellen und seine Konfiguration erhalten",
  "users.permission.TELTONIKA_PROVISION": "Teltonika-Router bereitstellen",
  "users.permissionDesc.TELTONIKA_PROVISION": "Einen Teltonika-Standort anhand der Seriennummer anlegen oder aktualisieren, sein VPN-Zertifikat ausstellen und seine Konfiguration erhalten",
  "users.permission.CLIENT_ACCOUNTS_MANAGE": "Kundenkonten verwalten",
  "users.permissionDesc.CLIENT_ACCOUNTS_MANAGE": "Kundenpersonen eines Kunden einladen, ändern und entfernen und „Als Kunde ansehen“ bei einer Veranstaltung öffnen. Nur für Mitglieder mit Zugriff auf alle Standorte",
}

export default users
